import { Octokit } from "octokit";
import { hashPassword } from '../utils/auth';
import imageCompression from 'browser-image-compression';

const octokit = new Octokit({ 
  auth: process.env.REACT_APP_GITHUB_TOKEN 
});

const REPO_OWNER = process.env.REACT_APP_REPO_OWNER;
const REPO_NAME = process.env.REACT_APP_REPO_NAME;

const IMAGES_DIR = 'product-images';
const CHECKS_DIR = 'checks';
const REVIEWS_IMAGES_DIR = 'reviews-images';
const MAIL_PHOTOS_DIR = 'mail-photos';

// Универсальный кодировщик для любых символов
const encodeBase64 = (data) => {
  if (typeof data !== 'string') {
    data = JSON.stringify(data);
  }
  try {
    return btoa(unescape(encodeURIComponent(data)));
  } catch (e) {
    return Buffer.from(data).toString('base64');
  }
};

// Вспомогательная функция для получения даты в формате YYYY-MM-DD
const getDateString = (date) => {
  return date.toISOString().split('T')[0];
};

// Вспомогательная функция для проверки и обновления attendance
const updateAttendance = (employee, currentDate) => {
  const attendance = employee.attendance || [];
  const lastLogin = employee.lastLogin ? new Date(employee.lastLogin) : null;
  const today = new Date(currentDate);
  const todayString = getDateString(today);

  // Если нет записей за сегодня, добавляем
  if (!attendance.find(record => record.date === todayString)) {
    attendance.push({
      date: todayString,
      status: 'work' // По умолчанию отмечаем как рабочий день
    });
  }

  // Если есть lastLogin, проверяем пропущенные дни
  if (lastLogin) {
    const oneDayMs = 24 * 60 * 60 * 1000;
    let checkDate = new Date(lastLogin);
    checkDate.setDate(checkDate.getDate() + 1); // Начинаем с следующего дня после lastLogin

    while (checkDate < today) {
      const checkDateString = getDateString(checkDate);
      if (!attendance.find(record => record.date === checkDateString)) {
        const dayOfWeek = checkDate.toLocaleString('en-US', { weekday: 'short' }).toLowerCase().slice(0, 3);
        const isRestDay = employee.schedule?.restDays?.includes(dayOfWeek);
        attendance.push({
          date: checkDateString,
          status: isRestDay ? 'rest' : 'absent'
        });
      }
      checkDate.setDate(checkDate.getDate() + 1);
    }
  }

  // Сортируем attendance по дате
  attendance.sort((a, b) => new Date(b.date) - new Date(a.date));
  return attendance;
};

// Функция сжатия изображения
const compressImage = async (file) => {
  const options = {
    maxSizeMB: 1, // Максимальный размер файла в мегабайтах
    maxWidthOrHeight: 1920, // Максимальная ширина или высота
    useWebWorker: true, // Использовать Web Worker для оптимизации
  };
  try {
    const compressedFile = await imageCompression(file, options);
    // Сохраняем оригинальное имя файла
    return new File([compressedFile], file.name, { type: compressedFile.type });
  } catch (error) {
    console.error("Расм сикишда хатолик кичикрок расм танланг:", error);
    // Если сжатие не удалось, возвращаем оригинальный файл
    return file;
  }
};

export const uploadImage = async (file) => {
  try {
    // Сжимаем изображение перед загрузкой
    const compressedFile = await compressImage(file);
    const fileName = `${IMAGES_DIR}/${Date.now()}_${compressedFile.name.replace(/\s+/g, '_')}`;
    const content = await readFileAsBase64(compressedFile);
    
    const { data } = await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${fileName}`,
      {
        message: `Добавлено изображение: ${compressedFile.name}`,
        content,
      }
    );

    return `https://raw.githubusercontent.com/${REPO_OWNER}/${REPO_NAME}/main/${fileName}`;
  } catch (error) {
    console.error("Юклашда хатолик:", error);
    throw new Error("Расм юклашда хатолик");
  }
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(',')[1]);
    reader.readAsDataURL(file);
  });
};

export const deleteImage = async (imageUrl) => {
  try {
    const path = imageUrl.split(`${REPO_NAME}/main/`)[1];
    const { data } = await octokit.request(
      `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`
    );
    
    await octokit.request(
      `DELETE /repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`,
      {
        message: `Удалено изображение: ${path.split('/').pop()}`,
        sha: data.sha,
      }
    );
  } catch (error) {
    console.error("Расм учирилган йоки топилмади");
  }
};

export const fetchData = async (filename) => {
  try {
    const response = await octokit.request(
      `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/${filename}`
    );
    
    const decodedContent = decodeURIComponent(
      atob(response.data.content)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
    );
    
    return JSON.parse(decodedContent);
  } catch (error) {
    console.error("Малумотлар юклашда хатолик:", error);
    return [];
  }
};

export const isUsernameTaken = async (username, excludeId = null) => {
  const employees = await fetchData('employees.json');
  return employees.some(
    emp => emp.username === username && emp.id !== excludeId
  );
};

export const createEmployee = async (newEmployee) => {
  try {
    const currentResponse = await octokit.request(
      `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`
    );
    
    const currentData = JSON.parse(atob(currentResponse.data.content));
    
    if (await isUsernameTaken(newEmployee.username)) {
      throw new Error('Бу никнейм уже ишлатилган');
    }

    const updatedData = [
      ...currentData,
      {
        ...newEmployee,
        password: hashPassword(newEmployee.password),
        role: 'employee',
        workLogs: [],
        attendance: [] // Инициализируем attendance
      }
    ];

    await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`,
      {
        message: 'Добавлен новый работник',
        content: encodeBase64(JSON.stringify(updatedData, null, 2)),
        sha: currentResponse.data.sha
      }
    );
    
    return true;
  } catch (error) {
    console.error("Аккаунт яратишда хатолик:", error);
    throw error;
  }
};

export const fetchClients = async (employeeId = null) => {
  try {
    const clients = await fetchData('clients.json');
    if (employeeId) {
      return clients.filter(client => 
        client.orders.some(order => order.employeeId === employeeId)
      );
    }
    return clients;
  } catch (error) {
    console.error("Мижозлар юклашда хатолик:", error);
    return [];
  }
};

export const saveClientWithProductsUpdate = async (clientData, productsUpdate) => {
  try {
    const [clientsRes, productsRes] = await Promise.all([
      octokit.request(`GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/clients.json`),
      octokit.request(`GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/products.json`)
    ]);

    const clients = JSON.parse(decodeURIComponent(escape(atob(clientsRes.data.content))));
    const products = JSON.parse(decodeURIComponent(escape(atob(productsRes.data.content))));

    const existingIndex = clients.findIndex(c => c.code === clientData.code);
    if (existingIndex >= 0) {
      clients[existingIndex] = { ...clients[existingIndex], ...clientData };
    } else {
      clients.push(clientData);
    }

    productsUpdate.forEach(({ productId, quantity }) => {
      const product = products.find(p => p.id === productId);
      if (product) product.stock -= quantity;
    });

    await Promise.all([
      octokit.request(`PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/clients.json`, {
        message: `Обновлен клиент ${clientData.nickname}`,
        content: encodeBase64(JSON.stringify(clients, null, 2)),
        sha: clientsRes.data.sha
      }),
      octokit.request(`PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/products.json`, {
        message: `Обновлены остатки товаров для клиента ${clientData.nickname}`,
        content: encodeBase64(JSON.stringify(products, null, 2)),
        sha: productsRes.data.sha
      })
    ]);

    return true;
  } catch (error) {
    console.error("Мураккаб саклаш хатолик:", error);
    throw error;
  }
};

export const saveClient = async (clientData) => {
  const clients = await fetchClients();
  const existingClientIndex = clients.findIndex(c => c.code === clientData.code);
  
  if (existingClientIndex >= 0) {
    clients[existingClientIndex] = {
      ...clients[existingClientIndex],
      ...clientData
    };
  } else {
    clients.push(clientData);
  }

  await saveData('clients.json', clients);
};

export const updateProductStock = async (productId, quantitySold) => {
  const products = await fetchProducts();
  const productIndex = products.findIndex(p => p.id === productId);
  
  if (productIndex >= 0) {
    products[productIndex].stock -= quantitySold;
    await saveData('products.json', products);
  }
};

export const fetchMails = async (employeeId = null) => {
  try {
    const mails = await fetchData('mails.json');
    
    if (employeeId) {
      return mails.filter(mail => mail.employeeId === employeeId);
    }
    
    return mails;
  } catch (error) {
    console.error("Почта юклашда хатолик:", error);
    return [];
  }
};

export const addMail = async (mailData) => {
  const mails = await fetchMails();
  const employeeId = localStorage.getItem('userId');
  
  const newMail = {
    id: `mail_${Date.now()}`,
    ...mailData,
    employeeId,
    createdAt: new Date().toISOString(),
    status: 'new'
  };

  const updatedMails = [...mails, newMail];
  await saveData('mails.json', updatedMails);
  return newMail;
};

export const updateMail = async (id, updatedData) => {
  const mails = await fetchMails();
  const updatedMails = mails.map(mail =>
    mail.id === id ? { ...mail, ...updatedData } : mail
  );
  await saveData('mails.json', updatedMails);
};

export const deleteMail = async (id) => {
  const mails = await fetchMails();
  const updatedMails = mails.filter(mail => mail.id !== id);
  await saveData('mails.json', updatedMails);
};

export const addMailPhoto = async (mailPhotoData) => {
  try {
    const mailPhotos = await fetchData('mailphotos.json');
    
    const newPhoto = {
      id: `photo_${Date.now()}`,
      mailId: mailPhotoData.mailId,
      imageUrl: mailPhotoData.imageUrl,
      createdAt: mailPhotoData.createdAt || new Date().toISOString(),
      status: 'new',
      sentAt: null
    };

    const updatedMailPhotos = [...mailPhotos, newPhoto];
    await saveData('mailphotos.json', updatedMailPhotos);
    return newPhoto;
  } catch (error) {
    console.error("Почта расми юклашда хатолик:", error);
    throw error;
  }
};

export const updateMailPhoto = async (photoId, updatedData) => {
  try {
    const mailPhotos = await fetchData('mailphotos.json');
    const updatedMailPhotos = mailPhotos.map(photo =>
      photo.id === photoId ? { ...photo, ...updatedData } : photo
    );

    await saveData('mailphotos.json', updatedMailPhotos);
    return true;
  } catch (error) {
    console.error("Почта расми янгилашда хатолик:", error);
    throw error;
  }
};

export const deleteMailPhoto = async (photoId) => {
  try {
    const mailPhotos = await fetchData('mailphotos.json');
    const photo = mailPhotos.find(p => p.id === photoId);
    if (!photo) {
      throw new Error('Почта расми топилмади');
    }

    const updatedMailPhotos = mailPhotos.filter(p => p.id !== photoId);
    await saveData('mailphotos.json', updatedMailPhotos);

    if (photo.imageUrl) {
      await deleteImage(photo.imageUrl);
    }
    return true;
  } catch (error) {
    console.error("Почта расми учиришда хатолик:", error);
    throw error;
  }
};

export const uploadMailPhoto = async (file) => {
  try {
    // Сжимаем изображение перед загрузкой
    const compressedFile = await compressImage(file);
    const fileName = `${MAIL_PHOTOS_DIR}/${Date.now()}_${compressedFile.name.replace(/\s+/g, '_')}`;
    const content = await readFileAsBase64(compressedFile);
    
    const { data } = await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${fileName}`,
      {
        message: `Почта расми кошилди: ${compressedFile.name}`,
        content,
      }
    );

    return `https://raw.githubusercontent.com/${REPO_OWNER}/${REPO_NAME}/main/${fileName}`;
  } catch (error) {
    console.error("Почта расми юклашда хатолик:", error);
    throw new Error("Почта расми юклашда хатолик");
  }
};

export const markMailAsProcessed = async (id) => {
  const mails = await fetchMails();
  const updatedMails = mails.map(mail =>
    mail.id === id ? { ...mail, status: 'processed' } : mail
  );
  await saveData('mails.json', updatedMails);
};

export const markMailAsCompleted = async (id) => {
  const mails = await fetchMails();
  const updatedMails = mails.map(mail =>
    mail.id === id ? { 
      ...mail, 
      status: 'completed',
      completedAt: new Date().toISOString()
    } : mail
  );
  await saveData('mails.json', updatedMails);
};

export const searchMailsByCode = async (code) => {
  const mails = await fetchMails();
  return mails.filter(mail => mail.code.includes(code));
};

export const markMailPhotoAsSent = async (photoId) => {
  try {
    const mailPhotos = await fetchData('mailphotos.json');
    const updatedMailPhotos = mailPhotos.map(photo =>
      photo.id === photoId ? { 
        ...photo, 
        status: 'sent',
        sentAt: new Date().toISOString()
      } : photo
    );

    await saveData('mailphotos.json', updatedMailPhotos);
    return true;
  } catch (error) {
    console.error("Почта расми статуси янгилашда хатолик:", error);
    throw error;
  }
};

export const getMailStats = async () => {
  const mails = await fetchMails();
  return {
    total: mails.length,
    new: mails.filter(mail => mail.status === 'new').length,
    processed: mails.filter(mail => mail.status === 'processed').length,
    completed: mails.filter(mail => mail.status === 'completed').length
  };
};

export const addClient = async (clientData) => {
  const clients = await fetchData('clients.json');
  const existingClient = clients.find(c => c.nickname === clientData.nickname);

  const newOrder = {
    id: `ord_${Date.now()}`,
    date: clientData.orderDate,
    products: clientData.products,
    location: clientData.location,
    employee: clientData.employee
  };

  const totalAmount = clientData.products.reduce(
    (sum, product) => sum + (product.price * product.quantity), 0
  );

  if (existingClient) {
    existingClient.orders.push(newOrder);
    existingClient.totalSpent += totalAmount;
    existingClient.lastOrderDate = clientData.orderDate;
  } else {
    clients.push({
      id: `cl_${Date.now()}`,
      nickname: clientData.nickname,
      code: clientData.code,
      orders: [newOrder],
      totalSpent: totalAmount,
      firstOrderDate: clientData.orderDate,
      lastOrderDate: clientData.orderDate
    });
  }

  await saveData('clients.json', clients);
};

export const fetchProducts = async () => {
  try {
    return await fetchData('products.json');
  } catch (error) {
    console.error("Махсулотлар юклашда хатолик:", error);
    return [];
  }
};

export const addProduct = async (productData) => {
  const products = await fetchProducts();
  const newProduct = {
    id: `prod_${Date.now()}`,
    ...productData,
  };
  const updatedProducts = [...products, newProduct];
  await saveData('products.json', updatedProducts);
};

export const updateProduct = async (id, updatedData) => {
  const products = await fetchProducts();
  const updatedProducts = products.map(product =>
    product.id === id ? { ...product, ...updatedData } : product
  );
  await saveData('products.json', updatedProducts);
};

export const deleteProduct = async (id) => {
  try {
    const products = await fetchProducts();
    const product = products.find(p => p.id === id);
    if (!product) {
      throw new Error('Махсулот топилмади');
    }

    // Удаляем изображение, если оно существует
    if (product.image) {
      await deleteImage(product.image);
    }

    const updatedProducts = products.filter(product => product.id !== id);
    await saveData('products.json', updatedProducts);
  } catch (error) {
    console.error("Махсулот учиришда хатолик:", error);
    throw error;
  }
};

const saveData = async (filename, data) => {
  const currentResponse = await octokit.request(
    `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/${filename}`
  );
  await octokit.request(
    `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${filename}`,
    {
      message: `Файл янгиланди ${filename}`,
      content: encodeBase64(JSON.stringify(data, null, 2)),
      sha: currentResponse.data.sha,
    }
  );
};

export const addEmployee = async (newEmployee) => {
  const employees = await fetchData('employees.json');
  
  if (!newEmployee.schedule) {
    throw new Error('Расписание белгиланмади');
  }

  const id = Date.now().toString();
  const updatedEmployees = [
    ...employees,
    {
      ...newEmployee,
      id,
      password: newEmployee.password,
      role: 'employee',
      stats: {
        clientsServed: 0,
        productsSold: 0,
        lastLogin: null
      },
      attendance: [] // Инициализируем attendance
    }
  ];

  const currentResponse = await octokit.request(
    `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`
  );

  await octokit.request(
    `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`,
    {
      message: `Добавлен работник ${newEmployee.name}`,
      content: encodeBase64(JSON.stringify(updatedEmployees, null, 2)),
      sha: currentResponse.data.sha
    }
  );
};

export const deleteEmployee = async (id) => {
  const employees = await fetchData('employees.json');
  const updatedEmployees = employees.filter(emp => emp.id !== id);

  const currentResponse = await octokit.request(
    `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`
  );

  await octokit.request(
    `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`,
    {
      message: `Удален работник ${id}`,
      content: encodeBase64(JSON.stringify(updatedEmployees, null, 2)),
      sha: currentResponse.data.sha
    }
  );
};

export const updateEmployee = async (id, updatedData) => {
  const employees = await fetchData('employees.json');

  if (updatedData.username && await isUsernameTaken(updatedData.username, id)) {
    throw new Error('Бу никнейм уже ишлатилган');
  }
  
  const updatedEmployees = employees.map(emp => 
    emp.id === id ? { 
      ...emp, 
      ...updatedData,
      attendance: updatedData.attendance || emp.attendance || [] // Сохраняем attendance
    } : emp
  );

  const currentResponse = await octokit.request(
    `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`
  );

  await octokit.request(
    `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/employees.json`,
    {
      message: `Ходим янгиланди ${id}`,
      content: encodeBase64(JSON.stringify(updatedEmployees, null, 2)),
      sha: currentResponse.data.sha
    }
  );
};

export const deleteCheckImage = async (imageUrl) => {
  try {
    const path = imageUrl.split(`${REPO_NAME}/main/`)[1];
    const { data } = await octokit.request(
      `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`
    );
    
    await octokit.request(
      `DELETE /repos/${REPO_OWNER}/${REPO_NAME}/contents/${path}`,
      {
        message: `Чек расмни учишилди: ${path.split('/').pop()}`,
        sha: data.sha,
      }
    );
    return true;
  } catch (error) {
    console.error("Чек расмини учиришда хатолик:", error);
    throw new Error("Чек расмини учиришда хатолик");
  }
};

export const updateCheckStatus = async (filename, checkUpdates) => {
  try {
    const currentResponse = await octokit.request(
      `GET /repos/${REPO_OWNER}/${REPO_NAME}/contents/${filename}`
    );
    
    const currentData = JSON.parse(decodeURIComponent(escape(atob(currentResponse.data.content))));
    
    const updatedData = currentData.map(item => {
      if (filename === 'clients.json') {
        return {
          ...item,
          orders: item.orders.map(order => ({
            ...order,
            checkImages: order.checkImages?.map(check => 
              check.url === checkUpdates.url ? { ...check, ...checkUpdates } : check
            )
          }))
        };
      } else if (filename === 'mails.json') {
        return {
          ...item,
          checkImages: item.checkImages?.map(check => 
            check.url === checkUpdates.url ? { ...check, ...checkUpdates } : check
          )
        };
      }
      return item;
    });
    
    await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${filename}`,
      {
        message: `Чек статуси янгиланди`,
        content: encodeBase64(JSON.stringify(updatedData, null, 2)),
        sha: currentResponse.data.sha,
      }
    );
    
    return true;
  } catch (error) {
    console.error("Чек янгилашда хатолик:", error);
    throw error;
  }
};

export const uploadCheckImage = async (file) => {
  try {
    // Сжимаем изображение перед загрузкой
    const compressedFile = await compressImage(file);
    const fileName = `${CHECKS_DIR}/${Date.now()}_${compressedFile.name.replace(/\s+/g, '_')}`;
    const content = await readFileAsBase64(compressedFile);
    
    const { data } = await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${fileName}`,
      {
        message: `Чек расми кошилди: ${compressedFile.name}`,
        content,
      }
    );

    return `https://raw.githubusercontent.com/${REPO_OWNER}/${REPO_NAME}/main/${fileName}`;
  } catch (error) {
    console.error("Чек расмини юклашда хатолик:", error);
    throw new Error("Чек расмини юклашда хатолик");
  }
};

export const getUserRole = async (email) => {
  const employees = await fetchData('employees.json');
  const user = employees.find(e => e.email === email);
  return user ? user.role : null;
};

export const markCodeAsUsed = async (codeId) => {
  const codes = await fetchCodes(true);
  const code = codes.find(c => c.id === codeId);
  
  if (!code) {
    throw new Error('Код топилмади');
  }

  const updatedCodes = codes.map(c => 
    c.id === codeId ? { 
      ...c, 
      isUsed: true, 
      usedAt: new Date().toISOString() 
    } : c
  );
  
  await saveData('codes.json', updatedCodes);
};

export const addCode = async (codeData) => {
  const codes = await fetchCodes(true);
  
  const codeExists = codes.some(c => c.value === codeData.value);
  if (codeExists) {
    throw new Error('Бундай кийматга ега код мавжуд');
  }

  const newCode = {
    id: `code_${Date.now()}`,
    ...codeData,
    createdAt: codeData.createdAt || new Date().toISOString(),
    isUsed: codeData.isUsed || false,
    usedAt: codeData.usedAt || null
  };

  const updatedCodes = [...codes, newCode];
  await saveData('codes.json', updatedCodes);
};

export const fetchCodes = async (includeUsed = false) => {
  try {
    const codes = await fetchData('codes.json');
    return codes;
  } catch (error) {
    console.error("Кодлар юклашда хатолик:", error);
    return [];
  }
};

export const updateLastLogin = async (userId) => {
  try {
    const employees = await fetchData('employees.json');
    const currentDate = new Date();
    const updatedEmployees = employees.map(emp => {
      if (emp.id === userId) {
        const updatedAttendance = updateAttendance(emp, currentDate);
        return { 
          ...emp, 
          lastLogin: currentDate.toISOString(),
          attendance: updatedAttendance
        };
      }
      return emp;
    });
    await saveData('employees.json', updatedEmployees);
  } catch (error) {
    console.error("Кириш вактини янгилашда хатолик:", error);
  }
};

export const updateCode = async (id, updatedData) => {
  const codes = await fetchCodes();
  const updatedCodes = codes.map(code =>
    code.id === id ? { ...code, ...updatedData } : code
  );
  await saveData('codes.json', updatedCodes);
};

export const deleteCode = async (id) => {
  const codes = await fetchCodes();
  const updatedCodes = codes.filter(code => code.id !== id);
  await saveData('codes.json', updatedCodes);
};

export const fetchTemplates = async (employeeId = null) => {
  try {
    const templates = await fetchData('templates.json');
    if (employeeId) {
      return templates.filter(template => template.employeeId === employeeId);
    }
    return templates;
  } catch (error) {
    console.error("Шаблонлар юклашда хатолик:", error);
    return [];
  }
};

export const addTemplate = async (templateData) => {
  const templates = await fetchTemplates();
  const employeeId = localStorage.getItem('userId');
  
  const newTemplate = {
    id: `template_${Date.now()}`,
    ...templateData,
    employeeId,
    createdAt: new Date().toISOString()
  };

  const updatedTemplates = [...templates, newTemplate];
  await saveData('templates.json', updatedTemplates);
  return newTemplate;
};

export const updateTemplate = async (id, updatedData) => {
  const templates = await fetchTemplates();
  const updatedTemplates = templates.map(template =>
    template.id === id ? { ...template, ...updatedData } : template
  );
  await saveData('templates.json', updatedTemplates);
};

export const deleteTemplate = async (id) => {
  const templates = await fetchTemplates();
  const updatedTemplates = templates.filter(template => template.id !== id);
  await saveData('templates.json', updatedTemplates);
};  

export const uploadReviewImage = async (file) => {
  try {
    // Сжимаем изображение перед загрузкой
    const compressedFile = await compressImage(file);
    const fileName = `${REVIEWS_IMAGES_DIR}/${Date.now()}_${compressedFile.name.replace(/\s+/g, '_')}`;
    const content = await readFileAsBase64(compressedFile);
    
    const { data } = await octokit.request(
      `PUT /repos/${REPO_OWNER}/${REPO_NAME}/contents/${fileName}`,
      {
        message: `Отзыв расми кошилди: ${compressedFile.name}`,
        content,
      }
    );

    return `https://raw.githubusercontent.com/${REPO_OWNER}/${REPO_NAME}/main/${fileName}`;
  } catch (error) {
    console.error("Отзыв расмни юклашда хатолик:", error);
    throw new Error("Отзыв расмни юклашда хатолик");
  }
};

export const fetchReviews = async () => {
  try {
    return await fetchData('reviews.json');
  } catch (error) {
    console.error("Отзыв юклашда хатолик:", error);
    return [];
  }
};

export const deleteReview = async (id) => {
  try {
    const reviews = await fetchReviews();
    const review = reviews.find(r => r.id === id);
    if (!review) {
      throw new Error('Отзыв топилмади');
    }

    if (review.imageUrl) {
      await deleteImage(review.imageUrl);
    }

    const updatedReviews = reviews.filter(r => r.id !== id);
    await saveData('reviews.json', updatedReviews);
    return true;
  } catch (error) {
    console.error("Отзыв учиришда хатолик:", error);
    throw error;
  }
};

export const addReview = async (reviewData) => {
  try {
    const reviews = await fetchReviews();
    const employeeId = localStorage.getItem('userId');
    const employeeName = localStorage.getItem('userName');
    
    const newReview = {
      id: `review_${Date.now()}`,
      ...reviewData,
      employeeId,
      employeeName,
      createdAt: new Date().toISOString()
    };

    const updatedReviews = [...reviews, newReview];
    await saveData('reviews.json', updatedReviews);
    return newReview;
  } catch (error) {
    console.error("Отзыв кошишда хатолик:", error);
    throw error;
  }
};

export const fetchAddresses = async () => {
  try {
    return await fetchData('addresses.json');
  } catch (error) {
    console.error("Манзил юклашда хатолик:", error);
    return [];
  }
};

export const addAddress = async (addressData) => {
  try {
    const addresses = await fetchAddresses();
    const newAddress = {
      id: `addr_${Date.now()}`,
      ...addressData,
    };
    const updatedAddresses = [...addresses, newAddress];
    await saveData('addresses.json', updatedAddresses);
    return newAddress;
  } catch (error) {
    console.error("Манзил кошганда хатолик:", error);
    throw error;
  }
};

export const updateAddress = async (id, updatedData) => {
  try {
    const addresses = await fetchAddresses();
    const updatedAddresses = addresses.map(addr =>
      addr.id === id ? { ...addr, ...updatedData } : addr
    );
    await saveData('addresses.json', updatedAddresses);
    return true;
  } catch (error) {
    console.error("Манзилни янгилашда хатолик:", error);
    throw error;
  }
};

export const deleteAddress = async (id) => {
  try {
    const addresses = await fetchAddresses();
    const updatedAddresses = addresses.filter(addr => addr.id !== id);
    await saveData('addresses.json', updatedAddresses);
    return true;
  } catch (error) {
    console.error("Манзил учиришда хатолик:", error);
    throw error;
  }
};